import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import USBLoadingSpinner from "@usb-shield/react-loading-spinner";
import api from "../api/axios";
import userStore from "../store/userStore";
import apiErrorStore from "../store/apiErrorStore";
import getCurrentEnv from "../utils/getCurrentEnv";
import mapDashboardType, { sortDashboards } from "../utils/mapDashboardType";
import reportListStore from "../store/reportListStore";
import ReportTypes from "./ReportTypes";
import buildingIcon from "../assets/buildingIcon.svg";
import MerchantList from "./MerchantList";
import merchantListStore from "../store/merchantListStore";
import { getBaseUrl } from "../utils/getBaseUrl";
import USBNotification from "@usb-shield/react-notification";
import EmergencyMode from "./EmergencyMode";
import ReportNotification from "./ReportNotification";
import ReportNotAvailable from "./ReportNotAvailable";
import useReportStore from "../store/reportStore";
import { ROUTE } from "../constants";
import {
  PREVIEW_REPORTS,
  PREVIEW_USER_NAMES,
  PREVIEW_GROUP_CODE,
} from "../constants";

// Extract the local-part (before "@") of an email/username, lowercased. This
// lets us match demo users by "firstname.lastname" regardless of the domain
// they log in with (@elavon.com, @usbank.com, etc.).
const getEmailName = (email) =>
  String(email || "")
    .split("@")[0]
    .toLowerCase();

const isPreviewUser = (email) => {
  const name = getEmailName(email);
  return PREVIEW_USER_NAMES.some((allowed) => allowed.toLowerCase() === name);
};

const Home = () => {
  const navigate = useNavigate();
  const env = getCurrentEnv();
  const [isLoader, setIsLoader] = useState(false);
  const [emergencyMode, setEmergencyMode] = useState({
    active: false,
    message: null,
  });
  const [isDataFetched, setIsDataFetched] = useState(false);
  const [isReportNotification, setIsReportNotification] = useState(false);
  const {
    setUserId,
    setSessionId,
    setUserName,
    setIsSuperUser,
    setUserAssociatedCustomerGroups,
    tokenReady,
  } = userStore();
  const { setErrorInFetchingData } = apiErrorStore();
  const { reportList, setReportList, setAllReportList } = reportListStore();
  const {
    merchantList,
    selectedMerchant,
    isFirstSelectedMerchant,
    setMerchantList,
    setSelectedMerchant,
    setIsFirstSelectedMerchant,
  } = merchantListStore();

  useEffect(() => {
    if (!tokenReady) return;

    const fetchData = async () => {
      setIsLoader(true);
      try {
        const responseData = await api.get(`${getBaseUrl(env)}/combinedData`);

        const { userProfile, userGroupCodes, allReports, emergencyMode } =
          responseData.data;

        if (!userProfile.data || !userGroupCodes || !allReports) {
          throwCustomError();
        }

        setUserId(userProfile.data.username);
        setSessionId(userProfile.data.sessionId);

        // Demo (preview) users are pinned to a single "Elavon Hotel" group,
        // ignoring the group codes returned by combinedData. This gives them
        // the single-group (non super-user) experience with 5 cards.
        // Preview behavior is restricted to IT and UAT environments only.
        const userEmail = userProfile.data?.email || userProfile.data.username;
        const isPreviewEnv = env === "uat";
        const isPreview = isPreviewEnv && isPreviewUser(userEmail);
        const effectiveGroupCodes = isPreview
          ? [PREVIEW_GROUP_CODE]
          : userGroupCodes;

        setUserAssociatedCustomerGroups(effectiveGroupCodes);

        createUserName(userProfile.data);

        if (emergencyMode && emergencyMode.active) {
          setEmergencyMode({
            active: emergencyMode.active,
            message: emergencyMode.message || "",
          });
        } else {
          setEmergencyMode({
            active: false,
            message: null,
          });

          // For preview users, only keep the pinned group's real reports, then
          // append the "coming soon" preview cards -> 5 cards total. Non-preview
          // users keep all reports unchanged.
          const baseReports = isPreview
            ? allReports.filter(
                (report) => report.customerGroupCode === PREVIEW_GROUP_CODE,
              )
            : allReports;

          // Outside UAT, show preview services whenever the API includes the
          // preview group. UAT remains restricted to the preview-user allow-list.
          const previewGroupCodes = isPreview
            ? effectiveGroupCodes
            : env !== "uat" &&
                allReports.some(
                  (report) => report.customerGroupCode === PREVIEW_GROUP_CODE,
                )
              ? [PREVIEW_GROUP_CODE]
              : [];

          // Clone per customer group so merchant filtering keeps working.
          const previewReports = previewGroupCodes.flatMap((groupCode) =>
            PREVIEW_REPORTS.map((report) => ({
              ...report,
              id: `${report.id}-${groupCode}`,
              customerGroupCode: groupCode,
            })),
          );

          const mappedReports = mapDashboardType([
            ...baseReports,
            ...previewReports,
          ]);
          setReportList(mappedReports);
          setAllReportList(mappedReports);

          if (selectedMerchant) {
            filterReportForMerchant();
          } else {
            setListData(mappedReports, effectiveGroupCodes);
          }
        }

        setErrorInFetchingData(false);
      } catch (err) {
        setErrorInFetchingData(true);
        // Route back in-app to PRE_LOGIN instead of bouncing to the external
        // CIAM login URL. That external redirect bypassed session-resume
        // logic and could strand SSO users on a CIAM-only URL.
        navigate(ROUTE.PRE_LOGIN);
      } finally {
        setIsLoader(false);
        setIsDataFetched(true);
      }
    };

    fetchData();
  }, [tokenReady, navigate, setUserName, setErrorInFetchingData]); // eslint-disable-line react-hooks/exhaustive-deps

  const throwCustomError = () => {
    const customError = new Error("Failed to fetch data from CIAM API's");
    customError.response = { status: 500 };
    throw customError;
  };

  const createUserName = ({ firstName, lastName }) => {
    const userName = `${firstName} ${lastName}`
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    setUserName(userName);
  };

  const setListData = (mappedReports, userGroupCodes) => {
    if (userGroupCodes.length > 1) {
      setIsSuperUser(true);
      setMerchantList(
        userGroupCodes.map((groupCode) => ({
          value: groupCode,
          label: groupCode,
        })),
      );
    } else {
      setIsSuperUser(false);
      checkIsKillSwitchEnabled(mappedReports);
      setReportList(mappedReports);
      setMerchantList([]);
    }
  };

  const onMerchantSelection = (selectedMerchant) => {
    useReportStore.getState().resetLastSelectedPage();
    setSelectedMerchant(selectedMerchant);
    setIsLoader(true);

    if (isFirstSelectedMerchant) {
      setIsFirstSelectedMerchant(false);
    }
    setTimeout(() => {
      filterReportForMerchant();
    }, 200);
  };

  const filterReportForMerchant = () => {
    const currentSelectedMerchant =
      merchantListStore.getState().selectedMerchant;

    const allReports = reportListStore.getState().allReportList;

    const reports = sortDashboards(
      allReports.filter(
        (report) => report.customerGroupCode === currentSelectedMerchant.value,
      ),
    );

    checkIsKillSwitchEnabled(reports);
    setReportList(reports);
    setIsLoader(false);
  };

  const checkIsKillSwitchEnabled = (reports) => {
    const isKillSwitchEnabled = reports.some(
      (report) => report.killSwitchEnabled,
    );
    setIsReportNotification(isKillSwitchEnabled);
  };

  const killSwitchMessage = (reportName, reportMessage) => {
    return (
      <div className="mt-2">
        <USBNotification status="danger" iconAssistiveText={{ label: "Error" }}>
          {reportName} - {reportMessage}
        </USBNotification>
      </div>
    );
  };

  return (
    <>
      {isLoader && (
        <div className="center-container center-loader">
          <USBLoadingSpinner datatestid="loading-spinner">
            Loading...
          </USBLoadingSpinner>
        </div>
      )}
      {!isLoader && emergencyMode.active && (
        <EmergencyMode message={emergencyMode.message} />
      )}
      {!isLoader && !emergencyMode.active && (
        <>
          {isDataFetched &&
            merchantList.length === 0 &&
            reportList.length === 0 && (
              <ReportNotAvailable hasMultipleMerchants={false} />
            )}
          {merchantList.length > 1 && (
            <>
              {isFirstSelectedMerchant && (
                <>
                  {/* Merchant Selection Card. */}
                  <div className="merchant-card-container pt-2">
                    <div className="merchant-card">
                      <div className="card-icon">
                        <img
                          src={buildingIcon}
                          alt="buildingIcon"
                          width="72"
                          height="72"
                        />
                      </div>
                      <div
                        className="card-title merchant-card-title"
                        title={"Please Select Merchant"}
                      >
                        <span className="card-title-text">
                          Please Select Merchant
                        </span>
                      </div>
                      <div className="card-description merchant-card-desc">
                        <span>
                          Please select the merchant you wish to view dashboards
                          for.
                        </span>
                        <span>
                          You can start typing or select from the dropdown. Upon
                          selecting, you'll get the option to choose from the
                          available dashboards for the selected merchant.
                        </span>
                        <span>
                          You can always select a different merchant from the
                          home.
                        </span>
                        <span>Privileged access for superusers only.</span>
                      </div>
                      <MerchantList
                        selectedMerchant={selectedMerchant}
                        merchantList={merchantList}
                        onMerchantSelect={onMerchantSelection}
                      />
                    </div>
                  </div>
                </>
              )}
              {!isFirstSelectedMerchant && (
                <>
                  {reportList.length === 0 && (
                    <ReportNotAvailable hasMultipleMerchants={true} />
                  )}
                  {reportList.length > 0 && (
                    <>
                      {isReportNotification && <ReportNotification />}
                      {reportList
                        .filter(
                          (report) =>
                            report.killSwitchEnabled && report.message,
                        )
                        .map((report) =>
                          killSwitchMessage(report.name, report.message),
                        )}
                    </>
                  )}
                  <div>
                    <div className="merchant-card-container pt-2 mb-1">
                      <div className="merchant-card card-ht-auto">
                        <div
                          className="card-title merchant-card-title"
                          title={"Please Select Merchant"}
                        >
                          <span className="card-title-text">
                            Please Select Merchant
                          </span>
                        </div>
                        <MerchantList
                          selectedMerchant={selectedMerchant}
                          merchantList={merchantList}
                          onMerchantSelect={onMerchantSelection}
                        />
                      </div>
                    </div>
                    {reportList.length > 0 && (
                      <ReportTypes
                        data-testid="dashboards"
                        reports={reportList}
                      />
                    )}
                  </div>
                </>
              )}
            </>
          )}
          {merchantList.length === 0 && reportList.length > 0 && (
            <>
              {isReportNotification && <ReportNotification />}
              {reportList
                .filter((report) => report.killSwitchEnabled && report.message)
                .map((report) =>
                  killSwitchMessage(report.name, report.message),
                )}
              <ReportTypes data-testid="dashboards" reports={reportList} />
            </>
          )}
        </>
      )}
    </>
  );
};

export default Home;
